import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Flame } from "lucide-react";
import client from "../lib/api";
import { Crest, SectionTitle, Pill, Loading, Card, Zig } from "../components/common";
import { AdSlot } from "../components/AdSlot";
import { NewsWire } from "../components/NewsWire";
import { LeagueTable } from "../components/LeagueTable";
import { InstallBanner } from "../components/InstallBanner";
import { TopScorers } from "../components/TopScorers";

const HERO = "https://customer-assets-v7afamib.emergentagent.net/job_dynamos-portal/artifacts/g5nnhisv_417379902_767900422037636_1210697243881940175_n.png";

// Temporary house video (bottom of home). Google AdSense gets priority if it fills the slot.
const HOME_BOTTOM_VIDEO = {
  video: "https://customer-assets-v7afamib.emergentagent.net/job_dynamos-portal/artifacts/snufuohp_PSL2026_Identity_Parade-2.mp4",
  alt: "PSL 2026 Identity Parade",
  testid: "house-ad-identity-parade",
};

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export default function HomePage() {
  const [data, setData] = useState(null);
  useEffect(() => { client.get("/home").then((r) => setData(r.data)).catch(() => setData(false)); }, []);
  if (!data) return <Loading label="Loading matchday" />;

  return (
    <div className="space-y-10" data-testid="home-page">
      {/* Install app (PWA) */}
      <InstallBanner />

      {/* Live ticker */}
      <div className="bg-ink text-gold rounded-sm overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-widest">
          <Flame size={14} className="text-live" /> Castle Lager PSL 2026 · Matchday 28 complete · Fantasy is live — pick your squad
        </div>
      </div>

      {/* Hero */}
      <section className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 relative rounded-sm overflow-hidden grain min-h-[320px] flex items-end" data-testid="hero">
          <img src={HERO} alt="Castle Lager PSL action" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-green via-green/85 to-green/30" />
          <div className="relative p-6 md:p-10 text-cream">
            <Pill tone="gold">2026 Season</Pill>
            <h1 className="font-head font-black text-3xl md:text-5xl tracking-tight mt-3 leading-[1.05]">
              The heartbeat of<br />Zimbabwean football
            </h1>
            <p className="mt-3 text-cream/80 max-w-md">Free news, official tables, club team sheets and a ZiG-priced fantasy game. No paywall, ever.</p>
            <div className="flex gap-3 mt-5">
              <Link to="/fantasy" data-testid="hero-fantasy-btn" className="px-5 py-2.5 bg-gold text-ink font-bold rounded-sm hover:bg-cream transition-colors">Play Fantasy</Link>
              <Link to="/table" className="px-5 py-2.5 border-2 border-gold text-gold font-bold rounded-sm hover:bg-gold hover:text-ink transition-colors">League Table</Link>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <AdSlot id="home.hero_companion" height={140} />
          <Card className="p-4 flex-1">
            <div className="text-gold text-xs font-bold uppercase tracking-widest mb-2">Fantasy 2026</div>
            <div className="font-head font-black text-2xl text-green">Build your squad</div>
            <p className="text-sm text-ink/60 mt-1">Budget <Zig value={data.zig_book?.budget ?? 45} className="font-bold" />. 15 players, max 3 per club.</p>
            <Link to="/fantasy/pick" className="inline-flex items-center gap-1 mt-3 text-green font-bold hover:text-gold">Pick your squad <ArrowRight size={16} /></Link>
          </Card>
        </div>
      </section>

      {/* Table snapshot + fixtures */}
      <section className="grid lg:grid-cols-2 gap-6">
        <div>
          <SectionTitle kicker="Standings" testid="home-table-title">League table</SectionTitle>
          <LeagueTable rows={data.table} compact />
          <Link to="/table" className="block text-center py-2.5 text-sm font-bold text-green hover:text-gold border border-t-0 border-pslborder rounded-b-sm">Full table →</Link>
        </div>
        <div>
          <SectionTitle kicker="Matchday" testid="home-fixtures-title">Fixtures & results</SectionTitle>
          <div className="space-y-2">
            {data.fixtures.map((f) => (
              <Link to={`/match/${f.id}`} key={f.id}>
                <Card className="p-3 flex items-center gap-2">
                  <div className="flex-1 flex items-center justify-end gap-2 text-sm font-semibold"><span className="truncate">{f.home_name}</span><Crest slug={f.home_slug} name={f.home_name} size={24} /></div>
                  <div className="px-3 font-head font-black text-green tabular">{f.status === "FT" ? `${f.home_score}-${f.away_score}` : <span className="text-xs text-ink/40 flex items-center gap-1"><Clock size={12} />{new Date(f.kickoff).toLocaleDateString("en-GB",{day:"numeric",month:"short"})}</span>}</div>
                  <div className="flex-1 flex items-center gap-2 text-sm font-semibold"><Crest slug={f.away_slug} name={f.away_name} size={24} /><span className="truncate">{f.away_name}</span></div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Top scorers */}
      <section data-testid="home-top-scorers">
        <TopScorers limit={5} />
        <Link to="/table" className="block text-center py-2.5 text-sm font-bold text-green hover:text-gold border border-t-0 border-pslborder rounded-b-sm">Full golden boot standings →</Link>
      </section>

      <AdSlot id="home.feed_native" height={90} />

      {/* News + updates */}
      <section className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <SectionTitle kicker="Latest" testid="home-news-title">News feed</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-4">
            {data.news.map((s) => (
              <Link to={`/news/${s.slug}`} key={s.id}>
                <Card className="overflow-hidden h-full hover:-translate-y-1 transition-transform">
                  <img src={s.hero_image} alt={s.title} className="w-full h-36 object-cover" />
                  <div className="p-4">
                    <div className="flex items-center gap-1.5">
                      <Pill tone="muted">News</Pill>
                      {s.opinion && <Pill tone="live">Opinion</Pill>}
                    </div>
                    <h3 className="font-head font-bold text-green mt-2 leading-snug">{s.title}</h3>
                    <p className="text-xs text-ink/50 mt-2">{s.author}</p>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
        <div>
          <NewsWire limit={6} />
        </div>
      </section>

      {/* Bottom auspice — house video (AdSense gets priority) */}
      <AdSlot id="home.bottom" height={220} house={HOME_BOTTOM_VIDEO} />
    </div>
  );
}
